// tslint:disable-next-line:import-name
import { Capacitor } from '@capacitor/core';
import config from '../config.general';
import {
	howNowRequestErrorMesageForClient,
	howNowRequestErrorMesageForClientDirector,
	howNowShowErrorMesageToClient
} from '../core/constants';
import { getApiDomain } from '../core/countryBasedConfig';
import { queryClient, versionCheckQueryKey } from '../core/reactQueryCore';
import ApiError from './ApiError';
import { getAppVersion } from './utils';

export const statuses = {
	ok: 200,
	badRequest: 400,
	unauthorized: 401,
	upgradeRequired: 426
};

/**
 * @description Defines the basic interface to the API
 */
export default abstract class ApiBase {
	constructor(apiRootUrl: string) {
		this.apiRootUrl = apiRootUrl;
	}

	apiRootUrl: string;

	public async ajax(
		method: string,
		path: string,
		data: unknown = null,
		signal: AbortSignal = null,
		useToken = true,
		withJsonContentType = true,
		withBlob = false
	) {
		let headers: Record<string, string> = {};
		if (withJsonContentType)
			headers = {
				'content-type': 'application/json',
				accept: 'application/json'
			};
		else if (!withBlob) headers = { accept: 'application/json' };

		if (useToken) {
			const accessToken = await this.getAccessToken();
			headers.Authorization = `Bearer ${accessToken}`;
		}

		const platform = Capacitor.getPlatform();
		headers.appName = config.appName;
		headers['X-Platform'] = platform;
		headers['X-App-Version'] = await getAppVersion();

		// eslint-disable-next-line no-undef
		const request: RequestInit = {
			headers,
			method,
			mode: 'cors'
		};
		if (data) {
			if (withJsonContentType) request.body = JSON.stringify(data);
			else request.body = data as BodyInit;
		}
		if (signal) request.signal = signal;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return new Promise<any>((resolve, reject) => {
			fetch(getApiDomain() + path, request)
				.then(async (response) => {
					if (withBlob) {
						if (response.ok) {
							const blob = await response.blob(); // Convert response to blob
							const imageUrl = URL.createObjectURL(blob); // Create object URL for the image
							resolve(imageUrl);
						} else {
							const text = await response.text();
							reject(new ApiError(response, text));
						}
						return;
					}

					const text = await response.text();
					const responseBody = response.headers.get('Content-Type')?.includes('application/json')
						? JSON.parse(text)
						: text;

					if (response.ok) {
						resolve(responseBody);
					} else {
						if (response.status === statuses.upgradeRequired) {
							// Force-feed AppVersionGate so it renders force-update without re-polling.
							queryClient.setQueryData(versionCheckQueryKey(platform, config.appName), responseBody);
						}

						const errorBody = response.headers.get('Content-Type')?.includes('application/problem+json')
							? (getFirstErrorMessage(text) ?? JSON.parse(text).title ?? responseBody)
							: responseBody;

						const finalErrorMessage =
							errorBody.includes(howNowRequestErrorMesageForClient) ||
							errorBody.includes(howNowRequestErrorMesageForClientDirector)
								? howNowShowErrorMesageToClient
								: errorBody;

						// Ensure we create a proper ApiError object instead of rejecting with a string
						reject(new ApiError(response, finalErrorMessage));
					}
				})
				.catch((e) => {
					// This leaves the promise unresolved, which is not a memory leak issue.
					// https://stackoverflow.com/questions/20068467/does-never-resolved-promise-cause-memory-leak
					// This solution is also why we need a .then() instead of await.
					if (e.name === 'AbortError') return;
					if (e.message === 'Failed to fetch' || e.message === 'Load failed') {
						// Network-level failures: "Failed to fetch" (Chrome/Android), "Load failed" (iOS Safari/WKWebView)
						reject(new Error('App maintenance is currently occurring. Please come back later.'));
						return;
					}
					// Ensure we always reject with an Error object, not a string
					const error = e instanceof Error ? e : new Error(typeof e === 'string' ? e : 'Unknown error occurred');
					reject(error);
				});
		});
	}

	abstract getAccessToken(): Promise<string>;
}

// Only show the first error message, eg. the first BE model state validation error, so that we can keep the app bottom error bar shorter.
// Usually, there should not be any BE model state errors returned because we have FE fields validation.
const getFirstErrorMessage = (response: string) => {
	const responseObject = JSON.parse(response);
	if (typeof responseObject.errors !== 'object') return null;

	const [firstErrorKey, firstErrorMessage] = Object.entries(responseObject.errors)[0];
	return `${firstErrorKey}: ${firstErrorMessage}`;
};
